/**
 * Overview tab — the "is this pool healthy right now" surface plus the rapid
 * interventions an operator reaches for mid-incident (boost, dampen, pause,
 * manual scale). Pure consumer: all mutations are handler props owned by the
 * route. Pool name and active state are edited inline on the pool list rows.
 */

import { useState } from 'react';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import { CopyButton } from '../shared';
import {
  backendDisplayState,
  provisioningBadgeClass,
  relativeMs,
  type InfraState,
  type PoolStats,
  type ProvisioningState,
} from './shared';

const DURATION_OPTIONS = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '240', label: '4 hours' },
  { value: '480', label: '8 hours' },
  { value: '1440', label: '24 hours (max)' },
];

export interface OverviewTabProps {
  poolId: string;
  stats: PoolStats | null;
  infraState: InfraState | null;
  provState: ProvisioningState | null;
  actionInProgress: string | null;
  scaleDesired: string;
  onScaleDesiredChange: (v: string) => void;
  onScale: () => void;
  onBoost: (slots: number, durationMinutes: number, reason?: string) => Promise<void>;
  onRemoveBoost: (id: number) => void;
  onDampen: (slots: number, durationMinutes: number, reason?: string) => Promise<void>;
  onRemoveDampen: (id: number) => void;
  onPause: () => void;
  onResume: () => void;
  onReprovisionBackend: (backendId: string, label: string) => void;
}

export function OverviewTab({
  poolId,
  stats,
  infraState,
  provState,
  actionInProgress,
  scaleDesired,
  onScaleDesiredChange,
  onScale,
  onBoost,
  onRemoveBoost,
  onDampen,
  onRemoveDampen,
  onPause,
  onResume,
  onReprovisionBackend,
}: OverviewTabProps) {
  const counts = stats?.counts;

  // Boost / Dampen forms are tab-local: magnitude, duration preset, reason.
  // These are expiring interventions on top of the scaling engine.
  const [boostSlots, setBoostSlots] = useState('');
  const [boostDuration, setBoostDuration] = useState('60');
  const [boostReason, setBoostReason] = useState('');
  const [dampenSlots, setDampenSlots] = useState('');
  const [dampenDuration, setDampenDuration] = useState('60');
  const [dampenReason, setDampenReason] = useState('');

  const handleBoost = async () => {
    const slots = parseInt(boostSlots, 10);
    const duration = parseInt(boostDuration, 10);
    if (isNaN(slots) || slots <= 0 || isNaN(duration) || duration <= 0) return;
    await onBoost(slots, duration, boostReason.trim() || undefined);
    // Reset magnitude and reason but keep duration (common operator workflow)
    setBoostSlots('');
    setBoostReason('');
  };

  const handleDampen = async () => {
    const slots = parseInt(dampenSlots, 10);
    const duration = parseInt(dampenDuration, 10);
    if (isNaN(slots) || slots <= 0 || isNaN(duration) || duration <= 0) return;
    await onDampen(slots, duration, dampenReason.trim() || undefined);
    setDampenSlots('');
    setDampenReason('');
  };

  const latest = stats?.scalingDecisions?.[0];
  const lastAction = stats?.scaleActions?.[0];

  return (
    <>
      <div className={styles.poolIdentity}>
        <div className={styles.identityItem}>
          <span className={styles.identityLabel}>Pool ID</span>
          <span className={styles.identityValue}>{poolId}</span>
          <CopyButton value={poolId} />
        </div>
      </div>
      {/* Capacity Pipeline */}
      <div className={styles.pipelineCard}>
        <h3 className={styles.pipelineCardTitle}>Capacity Pipeline</h3>
        <div className={styles.pipelineGrid}>
          {/* Infrastructure Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Infrastructure</div>
            <div className={styles.pipelineColumnSubtitle}>Machine Instances</div>
            {infraState?.scaling ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Machines Running</span>
                  <span className={styles.statValue}>{infraState.scaling.currentCapacity}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Machines Desired</span>
                  <span className={styles.statValue}>{infraState.scaling.desiredCapacity}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Min / Max</span>
                  <span className={styles.statValue}>{infraState.scaling.minSize} / {infraState.scaling.maxSize}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>State</span>
                  <span
                    className={infraState.scaling.isStable ? styles.statValue : styles.settlingText}
                    title={infraState.scaling.isStable
                      ? 'All backends at their desired capacity'
                      : 'Bringing capacity up or down — normal during a ramp'}
                  >
                    {infraState.scaling.isStable ? 'Stable' : 'Settling…'}
                  </span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No cloud infrastructure</div>
            )}
            {/* Per-backend breakdown */}
            {infraState?.health?.backends && infraState.health.backends.length > 0 && (
              <div className={styles.backendList}>
                {infraState.health.backends.map(b => {
                  const provBackend = provState?.backends?.find(pb => pb.backendId === b.backendId);
                  const label = `${b.cloud}/${b.region}`;
                  const display = backendDisplayState(b, provBackend?.status);
                  return (
                    <div key={b.backendId} className={styles.backendRow}>
                      <span className={styles.backendId}>{label}</span>
                      <span className={styles.statValue}>{b.currentCapacity}/{b.desiredCapacity}</span>
                      <span className={display.badgeClass} title={display.title}>
                        {display.label}
                      </span>
                      <button
                        className={styles.actionButton}
                        style={{ padding: '0 var(--space-2)', fontSize: 'var(--text-xs)', lineHeight: 'var(--leading-normal)' }}
                        disabled={!!actionInProgress}
                        onClick={() => onReprovisionBackend(b.backendId, label)}
                      >
                        {actionInProgress === `reprovision-${b.backendId}` ? '...' : 'Reprovision'}
                      </button>
                    </div>
                  );
                })}
                {infraState.health.backends.filter(b => b.lastError).map(b => {
                  const provBackend = provState?.backends?.find(pb => pb.backendId === b.backendId);
                  const display = backendDisplayState(b, provBackend?.status);
                  return (
                    <div key={`${b.backendId}-note`} className={display.noteClass}>{b.backendId}: {b.lastError}</div>
                  );
                })}
              </div>
            )}
            {/* Failed backends not in health (failed provisioning) */}
            {provState?.backends?.filter(pb =>
              pb.status === 'failed' &&
              !infraState?.health?.backends?.some(hb => hb.backendId === pb.backendId)
            ).map(pb => (
              <div key={pb.backendId} className={styles.backendRow} style={{ marginTop: 'var(--space-1)' }}>
                <span className={styles.backendId}>{pb.cloud}/{pb.backendId}</span>
                <span className={styles.provFailed}>FAIL</span>
                <button
                  className={styles.actionButton}
                  style={{ padding: '0 var(--space-2)', fontSize: 'var(--text-xs)', lineHeight: 'var(--leading-normal)' }}
                  disabled={!!actionInProgress}
                  onClick={() => onReprovisionBackend(pb.backendId, `${pb.cloud}/${pb.backendId}`)}
                >
                  {actionInProgress === `reprovision-${pb.backendId}` ? '...' : 'Retry'}
                </button>
                {pb.error && <div className={styles.backendError}>{pb.error}</div>}
              </div>
            ))}
            {/* Pending machines */}
            {(() => {
              const pendingMachines = Math.max(0, (infraState?.scaling?.currentCapacity ?? 0) - (counts?.total ?? 0));
              if (pendingMachines > 0) return (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <span className={styles.pendingBadge}>Pending: {pendingMachines} machine{pendingMachines !== 1 ? 's' : ''}</span>
                  <div className={styles.pendingNote}>Launched, not yet registered</div>
                </div>
              );
              return null;
            })()}
          </div>

          {/* Orchestrators Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Orchestrators</div>
            <div className={styles.pipelineColumnSubtitle}>Connected Agents</div>
            {counts ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Total</span>
                  <span className={styles.statValue}>{counts.total}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Available</span>
                  <span className={styles.statValue}>{counts.available}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Busy</span>
                  <span className={styles.statValue}>{counts.busy}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Reconciling</span>
                  <span className={styles.statValue}>{counts.reconciling}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Draining</span>
                  <span className={styles.statValue}>{counts.draining}</span>
                </div>
                {counts.pinned > 0 && (
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Pinned</span>
                    <span className={styles.statValue}>{counts.pinned}</span>
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No data</div>
            )}
          </div>

          {/* Session Slots Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Session Slots</div>
            <div className={styles.pipelineColumnSubtitle}>Demand &amp; Utilization</div>
            {counts ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Used</span>
                  <span className={styles.statValue}>{counts.usedSessionSlots} / {counts.totalSessionSlots}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Available</span>
                  <span className={styles.statValue}>{counts.availableSessionSlots}</span>
                </div>
                {(() => {
                  const desiredSessions = infraState?.scaling?.desiredSessionCapacity ?? infraState?.scaling?.desiredCapacity ?? 0;
                  const pendingSessions = Math.max(0, desiredSessions - counts.totalSessionSlots);
                  const effectiveSlots = counts.totalSessionSlots + pendingSessions;
                  if (pendingSessions > 0) return (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Effective</span>
                      <span className={styles.statValue} style={{ color: 'var(--text-tertiary)' }}>
                        {effectiveSlots} ({counts.totalSessionSlots} actual + {pendingSessions} pend.)
                      </span>
                    </div>
                  );
                  return null;
                })()}
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No data</div>
            )}
          </div>
        </div>

        {/* Provisioning footer */}
        <div className={styles.pipelineFooter}>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Provisioning</span>
            {provState ? (
              <span className={provisioningBadgeClass(provState.status)}>
                {provState.status.replace(/_/g, ' ')}
              </span>
            ) : (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>unknown</span>
            )}
          </div>
        </div>
      </div>

      {/* Scaling Status */}
      {latest && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Scaling Status</h2>
          </div>
          <div className={styles.pipelineGrid}>
            <div>
              <div className={styles.statLabel}>Last Evaluation</div>
              <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>{relativeMs(latest.at)}</div>
              <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)', fontWeight: 'var(--weight-semibold)' as any }}>
                <span className={
                  latest.action === 'scale-up' ? styles.provProvisioned :
                  latest.action === 'scale-down' ? styles.provFailed :
                  styles.provPending
                }>{latest.action}</span>
                {' '}{latest.currentSessionCapacity} → {latest.targetSessionCapacity} slots
              </div>
            </div>
            <div>
              <div className={styles.statLabel}>Reason</div>
              <div style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)', wordBreak: 'break-word' }}>{latest.reason}</div>
            </div>
            <div>
              <div className={styles.statLabel}>Last Executed Action</div>
              {lastAction ? (
                <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
                  {lastAction.direction === 'up' ? 'Scale-up' : 'Scale-down'} {relativeMs(lastAction.at)}
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    {lastAction.fromInstanceCount} → {lastAction.toInstanceCount} slots — {lastAction.reason}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)', color: 'var(--text-tertiary)' }}>None</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Interventions */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Interventions</h2>
        </div>
        <div className={styles.controlsGrid}>
          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Boost (Extra Capacity)</div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.6, margin: '0 0 var(--space-2) 0' }}>
              Add session slots <em>on top of</em> whatever autoscaling already wants, for a limited time. If the engine wants 10 and you boost +20, the pool gets 30. Multiple active boosts stack. Still clamped by the pool's max and by any active dampen. Expires automatically.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>+ Slots</span>
              <input
                className={styles.inputField}
                type="number"
                min="1"
                value={boostSlots}
                onChange={e => setBoostSlots(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Duration</span>
              <select
                className={styles.inputField}
                value={boostDuration}
                onChange={e => setBoostDuration(e.target.value)}
              >
                {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Reason</span>
              <input
                className={styles.inputField}
                type="text"
                value={boostReason}
                onChange={e => setBoostReason(e.target.value)}
                placeholder="e.g. marketing launch"
                maxLength={500}
              />
            </div>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={handleBoost}
                disabled={actionInProgress === 'boost' || !boostSlots}
              >
                {actionInProgress === 'boost' ? 'Boosting...' : 'Boost'}
              </button>
            </div>
            {stats?.boosts && stats.boosts.length > 0 && (
              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Active Boosts
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  {stats.boosts.map(b => {
                    const minsLeft = Math.max(0, Math.ceil((b.expiresAt - Date.now()) / 60000));
                    return (
                      <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', background: 'var(--bg-secondary, rgba(0,0,0,0.03))' }}>
                        <span>
                          <strong>+{b.addedSlots}</strong> — {b.reason} <span style={{ opacity: 0.6 }}>by {b.createdBy}, {minsLeft}m left</span>
                        </span>
                        <button
                          className={styles.actionButton}
                          onClick={() => onRemoveBoost(b.id)}
                          disabled={actionInProgress === `remove-boost-${b.id}`}
                        >
                          {actionInProgress === `remove-boost-${b.id}` ? '...' : 'Cancel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Dampen (Temporary Ceiling)</div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.6, margin: '0 0 var(--space-2) 0' }}>
              Cap session slots for a limited time. Ceiling = current − removed. Use for cost emergencies or load-test throttling. Expires automatically.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>− Slots</span>
              <input
                className={styles.inputField}
                type="number"
                min="1"
                value={dampenSlots}
                onChange={e => setDampenSlots(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Duration</span>
              <select
                className={styles.inputField}
                value={dampenDuration}
                onChange={e => setDampenDuration(e.target.value)}
              >
                {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Reason</span>
              <input
                className={styles.inputField}
                type="text"
                value={dampenReason}
                onChange={e => setDampenReason(e.target.value)}
                placeholder="e.g. cost emergency"
                maxLength={500}
              />
            </div>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={handleDampen}
                disabled={actionInProgress === 'dampen' || !dampenSlots}
              >
                {actionInProgress === 'dampen' ? 'Dampening...' : 'Dampen'}
              </button>
            </div>
            {stats?.dampens && stats.dampens.length > 0 && (
              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Active Dampens
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  {stats.dampens.map(d => {
                    const minsLeft = Math.max(0, Math.ceil((d.expiresAt - Date.now()) / 60000));
                    return (
                      <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', background: 'var(--bg-secondary, rgba(0,0,0,0.03))' }}>
                        <span>
                          <strong>−{d.removedSlots}</strong> — {d.reason} <span style={{ opacity: 0.6 }}>by {d.createdBy}, {minsLeft}m left</span>
                        </span>
                        <button
                          className={styles.actionButton}
                          onClick={() => onRemoveDampen(d.id)}
                          disabled={actionInProgress === `remove-dampen-${d.id}`}
                        >
                          {actionInProgress === `remove-dampen-${d.id}` ? '...' : 'Cancel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Manual Scale (Session Slots)</div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)', fontWeight: 'var(--weight-semibold)', margin: '0 0 var(--space-2) 0' }}>
              Caution — prefer <strong>Boost</strong> for temporary capacity increases. Manual scale disables autoscaling entirely and does not expire.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Desired</span>
              <input
                className={styles.inputField}
                type="number"
                min="0"
                value={scaleDesired}
                onChange={e => onScaleDesiredChange(e.target.value)}
                placeholder="0"
              />
            </div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.5, margin: 'var(--space-1) 0' }}>
              Machines = ceil(desired / backend maxConcurrent). Pauses autoscaling.
            </p>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={onScale}
                disabled={actionInProgress === 'scale' || !scaleDesired}
              >
                {actionInProgress === 'scale' ? 'Scaling...' : 'Apply'}
              </button>
            </div>
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Autoscaling</div>
            <div className={styles.overrideStatus}>
              <span className={styles.overrideLabel}>Status:</span>
              <span className={`${styles.overrideValue} ${stats?.override?.autoscalingPaused ? styles.overrideActive : styles.overrideInactive}`}>
                {stats?.override?.autoscalingPaused ? 'Paused' : 'Active'}
              </span>
            </div>
            {stats?.override?.autoscalingPaused && stats.override.pauseReason && (
              <div className={styles.overrideStatus}>
                <span className={styles.overrideLabel}>Reason:</span>
                <span className={styles.overrideValue}>{stats.override.pauseReason}</span>
              </div>
            )}
            {stats?.override?.scaling?.desiredCapacity != null && (
              <div className={styles.overrideStatus}>
                <span className={styles.overrideLabel}>Target:</span>
                <span className={styles.overrideValue}>{stats.override.scaling.desiredCapacity} session slots</span>
              </div>
            )}
            <div className={styles.controlActions}>
              {stats?.override?.autoscalingPaused ? (
                <button
                  className={styles.applyButton}
                  onClick={onResume}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'resume' ? 'Resuming...' : 'Resume'}
                </button>
              ) : (
                <button
                  className={styles.actionButton}
                  onClick={onPause}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'pause' ? 'Pausing...' : 'Pause'}
                </button>
              )}
              {(stats?.override?.scaling || stats?.override?.autoscalingPaused) && (
                <button
                  className={styles.dangerButton}
                  onClick={onResume}
                  disabled={!!actionInProgress}
                >
                  Clear All Overrides
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
